import { useState, useCallback } from 'react';
import LabelIcon from '@mui/icons-material/Label';
import { Box } from '@mui/material';

import { FormTitleBox, LoadingSkeleton, ConfirmationDialog, TableBodySkeleton } from '../../common/components';
import { useMovementConcepts, useDeleteMovementConcept } from '../../hooks/useMovementConcepts';
import { usePaginatedFilter } from '../../common/hooks/usePaginatedFilter';

import MovementConceptsFilters from './components/MovementConceptsFilters';
import MovementConceptsDataTable from './components/MovementConceptsDataTable';
import MovementConceptDialog from './components/MovementConceptDialog';

import { ITEMS_PER_PAGE } from './constants/movementConceptsConstants';

const filterMovementConcepts = (collection = [], term = '') => {
  const normalizedTerm = term?.toLowerCase().trim();

  return collection.filter((concept) => {
    return (
      !normalizedTerm ||
      concept.name?.toLowerCase().includes(normalizedTerm) ||
      concept.code?.toLowerCase().includes(normalizedTerm) ||
      concept.description?.toLowerCase().includes(normalizedTerm)
    );
  });
};

const MovementConcepts = () => {
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [conceptToDelete, setConceptToDelete] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const { data: movementConcepts = [], isLoading, error } = useMovementConcepts();
  const deleteMovementConceptMutation = useDeleteMovementConcept();

  const filterFn = useCallback(filterMovementConcepts, []);

  const {
    data: paginatedConcepts,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: movementConcepts,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn,
  });

  const handleCreate = () => {
    setSelectedConcept(null);
    setEditMode(false);
    setIsDialogOpen(true);
  };

  const handleEdit = (concept) => {
    setSelectedConcept(concept);
    setEditMode(true);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (concept) => {
    setConceptToDelete(concept);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!conceptToDelete) return;

    try {
      await deleteMovementConceptMutation.mutateAsync(conceptToDelete.uuid);
      setConceptToDelete(null);
      setIsDeleteDialogOpen(false);
    } catch (mutationError) {
      console.error('Error al eliminar concepto de movimiento:', mutationError);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedConcept(null);
    setEditMode(false);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setConceptToDelete(null);
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <div className="text-red-600">
          Error al cargar los conceptos de movimiento: {error.message}
        </div>
      </Box>
    );
  }

  return (
    <>
      <Box>
        <FormTitleBox
          title="Finanzas | Conceptos de Movimiento"
          showButton
          buttonText="Nuevo Concepto"
          buttonIcon={LabelIcon}
          onButtonClick={handleCreate}
        />

        <MovementConceptsFilters
          searchTerm={searchTerm}
          onSearchChange={handleSearch}
        />

        <MovementConceptsDataTable
          movementConcepts={paginatedConcepts}
          totalMovementConcepts={totalCount}
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
          LoadingComponent={TableBodySkeleton}
        />
      </Box>

      <MovementConceptDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        editMode={editMode}
        selectedMovementConcept={selectedConcept}
      />

      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Eliminar Concepto"
        content={`¿Está seguro que desea eliminar el concepto "${conceptToDelete?.name}"?`}
        isLoading={deleteMovementConceptMutation.isPending}
      />
    </>
  );
};

export default MovementConcepts;
